import { sha256 } from 'js-sha256'

import { fetchBinFile } from 'api'

export const filterInvalidDomains = (arr: string[]): string[] => {
  return arr.filter((i) => /^https?:\/\//.test(i))
}

interface FastestOriginOptions {
  timeoutMs?: number
}

export const getDataValUrl = (origin: string): string => {
  return `${origin.replace(/\/$/, '')}/data_val`
}

/**
 * 一次探測的完整結果。
 *
 * 之所以回傳結構化結果而非單一網址：`getFastestOrigin` 的三層 fallback 需要
 * 「最快通過 checksum 者」與「最快成功回應者」兩種資訊，而只回傳前者會讓後者無從取得
 * （除非再探測一輪）。讓 policy 留在呼叫端，探測本身只負責蒐集事實。
 *
 * 刻意用 union 而非三個並存的欄位：有人通過 checksum 時，其餘探測仍在飛，
 * 此刻的「最快回應者」只是一個不穩定的中途快照。把它做成型別上取不到，
 * 呼叫端就不可能誤用。另一支則保證 fallbackError 必定有值（全數拒絕才會走到）。
 */
type ProbeOutcome =
  | { checksumWinner: string }
  | { checksumWinner: null; fastestResponded: string | null; fallbackError: unknown }

/**
 * 併發探測所有網址，蒐集 checksum 贏家與最快回應者。
 * 本函式不做任何 fallback 決策、不 throw（空清單只是走到「無人通過」那一支）。
 */
async function probeOrigins(urls: string[], options: FastestOriginOptions = {}): Promise<ProbeOutcome> {
  const { timeoutMs = 5000 } = options
  const getResponseText = (response: any): string => {
    const data = response?.data ?? response
    if (typeof data === 'string') return data
    if (data instanceof ArrayBuffer) return new TextDecoder().decode(data)
    return JSON.stringify(data) ?? ''
  }

  const validUrls = filterInvalidDomains(urls)
  const respondedUrls: string[] = []
  const unavailableUrls = new Set<string>()

  const probeUrl = async (url: string): Promise<{ url: string; hasExpectedChecksum: boolean }> => {
    const controller = new AbortController()
    let isTimeout = false
    const timeoutId = setTimeout(() => {
      isTimeout = true
      unavailableUrls.add(url)
      controller.abort()
    }, timeoutMs)

    let response: any
    try {
      response = await fetchBinFile({ url, signal: controller.signal })
      // axios 只有 2xx response 會 resolve，因此這裡紀錄的是成功回應順序。
      respondedUrls.push(url)
    } catch (error) {
      unavailableUrls.add(url)
      if (isTimeout) {
        console.warn(`[origin test] request timeout: ${url}`)
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }

    const hostname = new URL(url).hostname
    const expectedChecksum = sha256(hostname).slice(0, 30)

    return {
      url,
      hasExpectedChecksum: getResponseText(response).includes(expectedChecksum),
    }
  }

  // 同時探測所有網址。
  const probes = validUrls.map(probeUrl)
  const checksumMatchedUrls = probes.map((probe) =>
    probe.then(({ url, hasExpectedChecksum }) => {
      if (unavailableUrls.has(url)) {
        throw new Error(`[origin test] unavailable url: ${url}`)
      }
      // 讓不符合 checksum 的成功回應進入 Promise.any 的 reject 流程，繼續等待其他網址。
      if (!hasExpectedChecksum) {
        throw new Error(`invalid url checksum: ${url}`)
      }

      return url
    })
  )

  try {
    return { checksumWinner: await Promise.any(checksumMatchedUrls) }
  } catch (error) {
    // 走到這裡代表全數拒絕，亦即所有 probe 都已 settle、respondedUrls 不會再變動，
    // 此時取 [0] 才是確定的「最快成功回應者」。原始 AggregateError 一併帶出供診斷。
    return {
      checksumWinner: null,
      fastestResponded: respondedUrls[0] ?? null,
      fallbackError: error,
    }
  }
}

/* 傳入多個測速網址，優先回傳最快通過 hostname checksum 驗證的網址。
優先順序：
1. 若有網址通過 checksum 驗證，回傳最快通過驗證的網址。
2. 若沒有網址通過 checksum，退回使用最快成功回應的網址。
3. 若完全沒有成功回應，退回第一個網址。
當網址回傳時間timeout，會將其視為回傳失敗
*/
export async function getFastestOrigin(urls: string[], options: FastestOriginOptions = {}): Promise<string> {
  const outcome = await probeOrigins(urls, options)

  // Case 1: 符合 checksum 驗證。
  if (outcome.checksumWinner !== null) {
    return outcome.checksumWinner
  }

  // Case 2: 若沒有網址通過 checksum，退回使用最快成功回應的網址。
  if (outcome.fastestResponded) {
    return outcome.fastestResponded
  }

  // Case 3: 若沒有任何網址成功回應，退回使用第一個網址。
  const firstUrl = filterInvalidDomains(urls)[0] ?? urls[0]
  if (!firstUrl) {
    throw Object.assign(new Error('请检查网络设置'), { title: '网络异常' })
  }

  console.warn(`[origin test] no http 200 response, fallback to first url: ${firstUrl}`, outcome.fallbackError)
  return firstUrl
}

/** 把 origin 清單轉成 /data_val 探測網址，並保留反查回原 origin 的對照表 */
const buildDataValUrlMap = (origins: string[]): { dataValUrls: string[]; originByDataValUrl: Map<string, string> } => {
  const originByDataValUrl = new Map<string, string>()
  const dataValUrls = filterInvalidDomains(origins).map((origin) => {
    const dataValUrl = getDataValUrl(origin)
    originByDataValUrl.set(dataValUrl, origin)
    return dataValUrl
  })

  return { dataValUrls, originByDataValUrl }
}

export async function getFastestOriginWithDataVal(
  origins: string[],
  options: FastestOriginOptions = {}
): Promise<string> {
  const { dataValUrls, originByDataValUrl } = buildDataValUrlMap(origins)
  const fastestDataValUrl = await getFastestOrigin(dataValUrls, options)

  return originByDataValUrl.get(fastestDataValUrl) ?? fastestDataValUrl
}

/**
 * 只接受「通過 checksum 驗證」的線路，選不出來就回傳 null。
 *
 * 與 getFastestOriginWithDataVal 的差別：後者永遠會給出一個網址（必要時退回清單第一個），
 * 呼叫端因此無法分辨「測速選出的」與「fallback 的」。分享網域選線需要這個分辨能力——
 * 測不出來時要退回隨機挑選以分散流量，而不是讓所有使用者集中打清單第一條。
 *
 * 亦刻意不採用 fastestResponded：403 會在 CloudFront edge 直接被擋下（實測 ~0.04s），
 * 遠快於真正回源的線路（~0.9s），把它們納入比快會讓壞線路永遠勝出。
 */
export async function getFastestOriginWithDataValOrNull(
  origins: string[],
  options: FastestOriginOptions = {}
): Promise<string | null> {
  const { dataValUrls, originByDataValUrl } = buildDataValUrlMap(origins)

  if (dataValUrls.length === 0) {
    return null
  }

  const outcome = await probeOrigins(dataValUrls, options)
  if (outcome.checksumWinner === null) {
    return null
  }

  // 空字串不得流出：呼叫端以 null 觸發降級，falsy 但非 null 的值會繞過該判斷。
  const origin = originByDataValUrl.get(outcome.checksumWinner) ?? outcome.checksumWinner

  return origin || null
}
